import React from "react";
import { useNavigate } from "react-router-dom";
import { Box, Container, Typography, Button } from "@mui/material";
import { ErrorOutline, Home, ArrowBack } from "@mui/icons-material";
import { gradients } from "../../utils/theme";

/**
 * 404 Not Found Page
 *
 * Displays when user navigates to a non-existent route
 */
const NotFound: React.FC = () => {
  const navigate = useNavigate();

  const handleGoHome = () => {
    navigate("/app/dashboard", { replace: true });
  };

  const handleGoBack = () => {
    navigate(-1);
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background:
          "linear-gradient(135deg, #1a237e 0%, #283593 50%, #3949ab 100%)",
        position: "relative",
        overflow: "hidden",
        "&::before": {
          content: '""',
          position: "absolute",
          top: "-50%",
          left: "-50%",
          width: "200%",
          height: "200%",
          background:
            "radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)",
          animation: "pulse 15s ease-in-out infinite",
        },
        "@keyframes pulse": {
          "0%, 100%": {
            transform: "scale(1)",
            opacity: 0.5,
          },
          "50%": {
            transform: "scale(1.2)",
            opacity: 0.8,
          },
        },
      }}
    >
      <Container maxWidth="sm" sx={{ position: "relative", zIndex: 1 }}>
        <Box
          sx={{
            textAlign: "center",
            bgcolor: "background.paper",
            borderRadius: 4,
            p: { xs: 4, sm: 6 },
            boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
          }}
        >
          {/* Error Icon */}
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: 120,
              height: 120,
              borderRadius: "50%",
              background: "linear-gradient(135deg, #1a237e 0%, #3949ab 100%)",
              mb: 3,
              boxShadow: "0 8px 32px rgba(26, 35, 126, 0.3)",
            }}
          >
            <ErrorOutline sx={{ fontSize: 64, color: "white" }} />
          </Box>

          {/* 404 Text */}
          <Typography
            variant="h1"
            sx={{
              fontSize: { xs: "4rem", sm: "6rem" },
              fontWeight: 700,
              background: gradients.navyLux,
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              mb: 2,
            }}
          >
            404
          </Typography>

          {/* Message */}
          <Typography
            variant="h5"
            sx={{
              fontWeight: 600,
              color: "text.primary",
              mb: 2,
            }}
          >
            Page Not Found
          </Typography>

          <Typography
            variant="body1"
            sx={{
              color: "text.secondary",
              mb: 4,
            }}
          >
            The page you're looking for doesn't exist or has been moved.
          </Typography>

          {/* Action Buttons */}
          <Box
            sx={{
              display: "flex",
              gap: 2,
              justifyContent: "center",
              flexWrap: "wrap",
            }}
          >
            <Button
              variant="contained"
              size="large"
              startIcon={<Home />}
              onClick={handleGoHome}
              sx={{
                background: "linear-gradient(135deg, #1a237e 0%, #3949ab 100%)",
                color: "white",
                px: 4,
                py: 1.5,
                fontWeight: 600,
                textTransform: "none",
                boxShadow: "0 4px 16px rgba(26, 35, 126, 0.3)",
                "&:hover": {
                  background:
                    "linear-gradient(135deg, #0d1642 0%, #283593 100%)",
                  transform: "translateY(-2px)",
                  boxShadow: "0 6px 20px rgba(26, 35, 126, 0.4)",
                },
                transition: "all 0.2s ease-in-out",
              }}
            >
              Go to Dashboard
            </Button>

            <Button
              variant="outlined"
              size="large"
              startIcon={<ArrowBack />}
              onClick={handleGoBack}
              sx={{
                borderColor: "#1a237e",
                color: "#1a237e",
                px: 4,
                py: 1.5,
                fontWeight: 600,
                textTransform: "none",
                "&:hover": {
                  borderColor: "#0d1642",
                  bgcolor: "rgba(26, 35, 126, 0.04)",
                },
              }}
            >
              Go Back
            </Button>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default NotFound;
